import { InformationCircleIcon } from "@heroicons/react/16/solid";
import {
    CalendarDate,
    IChangeTrackOverrideable,
    isCrmAuthor,
    User,
    UserId,
} from "@sp-crm/core";
import { ProductBrandingContainer } from "components/app/product-branding-container";
import * as React from "react";
import { connect } from "react-redux";
import { ResponsiveMode } from "store/reducers/responsive";
import { ApplicationState } from "store/state";
import util from "../../util";
import { ChangeDateCallout } from "../change-date-callout/change-date-callout";

interface BylineHeaderEditableInboundProps {
    showUpdatedBy: boolean;
    createdAtDateModified: (newCreatedAtDate: Date) => void;
    entity: IChangeTrackOverrideable;
    createdByLabel?: string;
}

interface BylineHeaderEditableProps {
    createdAt?: Date;
    createdBy?: User;
    updatedAt?: Date;
    updatedBy?: User;
    createdById?: UserId;
    updatedById?: UserId;

    originalCreatedAt?: Date;
    createdAtDateModified: (newCreatedAtDate: Date) => void;
    showUpdatedBy: boolean;
    createdByLabel?: string;
    closedAt?: CalendarDate;
    responsiveMode: ResponsiveMode;
}

class BylineHeaderEditable extends React.Component<
    BylineHeaderEditableProps,
    { isExpanded: boolean }
> {
    static defaultProps = { createdByLabel: "Created" };

    constructor(props: BylineHeaderEditableProps) {
        super(props);
        this.state = { isExpanded: false };
    }

    toggleExpanded = () => {
        this.setState(prevState => ({ isExpanded: !prevState.isExpanded }));
    };

    // Mobile-friendly short timestamp
    getShortTimestamp(date: Date): string {
        const now = new Date();
        const diffInHours = Math.floor(
            (now.getTime() - date.getTime()) / (1000 * 60 * 60),
        );

        if (diffInHours < 1) {
            const diffInMinutes = Math.floor(
                (now.getTime() - date.getTime()) / (1000 * 60),
            );
            return `${diffInMinutes}m`;
        } else if (diffInHours < 24) {
            return `${diffInHours}h`;
        } else {
            const diffInDays = Math.floor(diffInHours / 24);
            return `${diffInDays}d`;
        }
    }

    modifyCreated(): JSX.Element {
        if (!this.props.createdAtDateModified) return null;

        return (
            <div className="date-picker-callout-anchor">
                <ChangeDateCallout
                    calloutCaption={`Choose the ${this.props.createdByLabel} Date:`}
                    revertBackToDate={this.props.originalCreatedAt}
                    revertBackDateLabel="*actual date created was"
                    initialDatePickerValue={this.props.createdAt}
                    onDateChanged={newDate => this.props.createdAtDateModified(newDate)}
                />
            </div>
        );
    }

    created(productName: string, isMobile: boolean = false): JSX.Element {
        if (this.props.createdAt && this.props.createdBy) {
            const timestamp = isMobile
                ? this.getShortTimestamp(this.props.createdAt)
                : this.props.originalCreatedAt === this.props.createdAt
                ? util.date.ago(this.props.createdAt)
                : util.date.daysAgo(this.props.createdAt);

            return (
                <div className="created">
                    <span className="whitespace-nowrap">
                        {isMobile ? "Created by" : `${this.props.createdByLabel} by`}{" "}
                        {util.user.displayName(this.props.createdBy)}
                    </span>
                    {this.props.createdAt && (
                        <span className="text-gray-500 ml-1">{timestamp}</span>
                    )}
                </div>
            );
        }
        if (this.props.createdAt && isCrmAuthor(this.props.createdById)) {
            const timestamp = isMobile
                ? this.getShortTimestamp(this.props.createdAt)
                : this.props.originalCreatedAt === this.props.createdAt
                ? util.date.ago(this.props.createdAt)
                : util.date.daysAgo(this.props.createdAt);

            return (
                <div className="created">
                    <span className="whitespace-nowrap">
                        {isMobile ? "Created by" : `${this.props.createdByLabel} by`}{" "}
                        {productName}
                    </span>
                    {this.props.createdAt && (
                        <span className="text-gray-500 ml-1">{timestamp}</span>
                    )}
                </div>
            );
        }
        if (this.props.createdBy) {
            return (
                <div className="created">
                    <span className="whitespace-nowrap">
                        {isMobile ? "Created by" : `${this.props.createdByLabel} by`}{" "}
                        {util.user.displayName(this.props.createdBy)}
                    </span>
                </div>
            );
        }
        if (this.props.createdAt) {
            const timestamp = isMobile
                ? this.getShortTimestamp(this.props.createdAt)
                : this.props.originalCreatedAt === this.props.createdAt
                ? util.date.ago(this.props.createdAt)
                : util.date.daysAgo(this.props.createdAt);

            return (
                <div className="created">
                    <span className="whitespace-nowrap">
                        {isMobile ? "Created" : this.props.createdByLabel}
                    </span>
                    <span className="text-gray-500 ml-1">{timestamp}</span>
                </div>
            );
        }
        return null;
    }

    updated(productName: string, isMobile: boolean = false): JSX.Element {
        if (!this.props.showUpdatedBy) {
            return null;
        }

        if (this.props.updatedAt && this.props.updatedBy) {
            const timestamp = isMobile
                ? this.getShortTimestamp(this.props.updatedAt)
                : util.date.ago(this.props.updatedAt);

            return (
                <div className="updated">
                    <span className="whitespace-nowrap">
                        {isMobile ? "Updated by" : "Updated by"}{" "}
                        {util.user.displayName(this.props.updatedBy)}
                    </span>
                    <span className="text-gray-500 ml-1">{timestamp}</span>
                </div>
            );
        }

        if (this.props.updatedAt && isCrmAuthor(this.props.updatedById)) {
            const timestamp = isMobile
                ? this.getShortTimestamp(this.props.updatedAt)
                : util.date.ago(this.props.updatedAt);

            return (
                <div className="updated">
                    <span className="whitespace-nowrap">
                        {isMobile ? "Updated by" : "Updated by"} {productName}
                    </span>
                    <span className="text-gray-500 ml-1">{timestamp}</span>
                </div>
            );
        }

        if (this.props.updatedBy) {
            return (
                <div className="updated">
                    <span className="whitespace-nowrap">
                        {isMobile ? "Updated by" : "Updated by"}{" "}
                        {util.user.displayName(this.props.updatedBy)}
                    </span>
                </div>
            );
        }
        if (this.props.updatedAt) {
            const timestamp = isMobile
                ? this.getShortTimestamp(this.props.updatedAt)
                : util.date.ago(this.props.updatedAt);

            return (
                <div className="updated">
                    <span className="whitespace-nowrap">
                        {isMobile ? "Updated" : "Updated"}
                    </span>
                    <span className="text-gray-500 ml-1">{timestamp}</span>
                </div>
            );
        }
        return null;
    }

    closed(): JSX.Element {
        if (!this.props.closedAt) {
            return <></>;
        }
        return (
            <div>Closed At {util.date.daysAgo(this.props.closedAt.toMoment(), "")}</div>
        );
    }

    render() {
        return (
            <ProductBrandingContainer>
                {branding => {
                    const productName = branding?.productName || "";
                    const isMobile = this.props.responsiveMode < ResponsiveMode.large;

                    if (isMobile) {
                        return (
                            <div className="flex items-center justify-end space-x-2">
                                {/* Mobile collapsed view */}
                                {!this.state.isExpanded && (
                                    <button
                                        onClick={this.toggleExpanded}
                                        className="flex items-center justify-center w-8 h-8 rounded-full bg-gray-100 hover:bg-gray-200 transition-colors"
                                        aria-label="Show author information">
                                        <InformationCircleIcon className="w-4 h-4 text-gray-600" />
                                    </button>
                                )}

                                {/* Mobile expanded view */}
                                {this.state.isExpanded && (
                                    <div className="flex flex-col items-end space-y-1 bg-white rounded-lg p-3">
                                        <div className="flex items-center justify-end w-full mb-2">
                                            <button
                                                onClick={this.toggleExpanded}
                                                className="text-gray-400 hover:text-gray-600"
                                                aria-label="Hide author information">
                                                ×
                                            </button>
                                        </div>
                                        <div className="flex flex-col space-y-1 text-sm">
                                            <div className="flex space-x-1">
                                                <div>
                                                    {this.created(productName, true)}
                                                </div>
                                                <div>{this.modifyCreated()}</div>
                                            </div>
                                            {this.updated(productName, true)}
                                            {this.closed()}
                                        </div>
                                    </div>
                                )}
                            </div>
                        );
                    }

                    // Desktop view (unchanged)
                    return (
                        <div className="flex-row byline-header-control">
                            <div className="flex-column-align-end">
                                <div className="flex-row-no-bottom-margin byline-date-row">
                                    {this.created(productName)}
                                </div>
                                <div className="flex-row-no-bottom-margin byline-date-row">
                                    {this.updated(productName)}
                                </div>
                                <div className="flex-row-no-bottom-margin byline-date-row">
                                    {this.closed()}
                                </div>
                            </div>
                            <div className="flex-column">{this.modifyCreated()}</div>
                        </div>
                    );
                }}
            </ProductBrandingContainer>
        );
    }
}

function mapStateToProps(
    state: ApplicationState,
    ownProps: BylineHeaderEditableInboundProps,
): BylineHeaderEditableProps {
    const entity = ownProps.entity as IChangeTrackOverrideable;
    return {
        createdAt: entity.dateAdded,
        createdBy: state.users.users[entity.createdBy],
        createdById: entity.createdBy,
        updatedAt: entity.updatedAt,
        updatedBy: state.users.users[entity.updatedBy],
        updatedById: entity.updatedBy,
        originalCreatedAt: entity._dateAdded,
        createdAtDateModified: ownProps.createdAtDateModified,
        showUpdatedBy: ownProps.showUpdatedBy,
        createdByLabel: ownProps.createdByLabel,
        closedAt: entity.statusClosedDate,
        responsiveMode: state.responsive.mode,
    };
}
const control = connect(mapStateToProps)(BylineHeaderEditable);
export { control as BylineHeaderEditable };
